import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { PLAYBOOK_MAX_LENGTH, type PlaybookDto } from '@signalvote/shared';
import { usePlaybook, useSavePlaybook, isVersionConflict } from '@/hooks/queries';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import defaultPlaybook from './defaultPlaybook.md?raw';

const CONFLICT_MESSAGE =
  'Your playbook changed somewhere else, maybe in another tab. Your text is still here. Save again to replace that version with it.';

const count = (n: number) => n.toLocaleString('en-US');

export function PlaybookEditor() {
  const { data: playbook, isLoading, error: loadError } = usePlaybook();

  if (loadError) {
    return <p className="text-sm text-destructive">{loadError.message}</p>;
  }
  if (isLoading || !playbook) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading...
      </div>
    );
  }
  return <PlaybookForm playbook={playbook} />;
}

function PlaybookForm({ playbook }: { playbook: PlaybookDto }) {
  const { mutateAsync: save, isPending: saving } = useSavePlaybook();
  // null until the user edits, so a refetch after a save or a conflict shows the server's text.
  const [draft, setDraft] = useState<string | null>(null);
  const [confirmingPrefill, setConfirmingPrefill] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const text = draft ?? playbook.text;
  const over = text.length > PLAYBOOK_MAX_LENGTH;
  const dirty = draft !== null && draft !== playbook.text;

  const edit = (next: string) => {
    setError(null);
    setDraft(next);
  };

  const startFromOurs = () => {
    if (text.trim() === '') edit(defaultPlaybook);
    else setConfirmingPrefill(true);
  };

  const submit = async () => {
    setError(null);
    try {
      await save({ text, version: playbook.version });
      setDraft(null);
    } catch (e) {
      setError(
        isVersionConflict(e) ? CONFLICT_MESSAGE : e instanceof Error ? e.message : "Couldn't save your playbook. Try again.",
      );
    }
  };

  return (
    <Card>
      <CardContent className="space-y-3 pt-6">
        <label htmlFor="playbook-text" className="text-sm font-medium">
          Your playbook
        </label>
        <textarea
          id="playbook-text"
          value={text}
          onChange={(e) => edit(e.target.value)}
          rows={20}
          disabled={saving}
          placeholder="How you'd coach a first-time candidate: what to do when, the numbers that mean on track, where you stand on signs and mail, and how you talk."
          className="w-full resize-y rounded-md border border-input bg-background px-3 py-2 font-mono text-[13px] leading-5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        />
        <p className={cn('text-xs tabular-nums', over ? 'text-destructive' : 'text-muted-foreground')}>
          <span>{`${count(text.length)} / ${count(PLAYBOOK_MAX_LENGTH)}`}</span>
          {over && <span>{` (${count(text.length - PLAYBOOK_MAX_LENGTH)} over the limit)`}</span>}
        </p>

        {confirmingPrefill && (
          <div className="flex flex-wrap items-center gap-2 rounded-md border bg-muted/50 px-3 py-2">
            <p className="flex-1 text-sm">Replace what's in the box with our playbook?</p>
            <Button
              size="sm"
              onClick={() => {
                setConfirmingPrefill(false);
                edit(defaultPlaybook);
              }}
            >
              Replace
            </Button>
            <Button size="sm" variant="outline" onClick={() => setConfirmingPrefill(false)}>
              Cancel
            </Button>
          </div>
        )}

        {error && <p className="text-sm text-destructive">{error}</p>}

        <div className="flex flex-wrap items-center gap-2">
          <Button size="sm" onClick={submit} disabled={!dirty || over || saving}>
            {saving ? (
              <>
                <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" /> Saving...
              </>
            ) : (
              'Save'
            )}
          </Button>
          <Button size="sm" variant="outline" onClick={startFromOurs} disabled={saving || confirmingPrefill}>
            Start from our playbook
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
